import net from 'steamer-net/dist/index';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { isLogin } from 'utils';
import SnackbarMobile from '@tencent/dui-mobile/lib/components/Snackbar';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import ua from '@tencent/docs-user-agent';
import _has from 'lodash/has';
import _get from 'lodash/get';
import i18n from '@i18next/i18n';
import { observable } from 'mobx';
import { SlideDataServices, scheduleAfterFirstSlideDisplay, onSlideAppReady } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IWatermarkManager } from '@src/slide/application/service/watermark-manager/core/interface';
import React, { lazy } from 'react';
import LazyBoundary from '@src/framework/frame/components/LazyBoundary';
import { getXSRF } from '@src/slide/domain/slide-app/utils/cookie-utils';
import { GenerateSVG, WatermarkDialogManager } from '@tencent/docs-scenario-components-watermark-generate';

const { isMobile } = ua;
export const Snackbars = isMobile ? SnackbarMobile : Snackbar;
const fontColor = '#999999';
const waterMarkSizeForExportImage = {
    1: 'large',
    2: 'small',
};

export default class WatermarkManager implements IWatermarkManager {
    @observable
    public watermarkSVGInfo;
    public watermarkInfo;
    public nickName: string;
    private isNicknameUndefined;
    private playerSVGInfo;
    private pageWidth: number;
    private pageHeight: number;
    private clientVarsWaterMark;
    private scWatermark;
    private readonly LazyWatermarkMask = lazy(() => scheduleAfterFirstSlideDisplay(() => import(/* webpackChunkName: "lazyComponents" */ './component/watermark-mask')));

    public constructor() {
        this.isNicknameUndefined = true;
        this.watermarkSVGInfo = null;
        this.playerSVGInfo = null;
        this.pageWidth = 700;
        this.pageHeight = 700;

        if (window.clientVars) {
            this.updateWaterMarkInfo(window.clientVars?.watermark);
        } else {
            onSlideAppReady(() => this.updateWaterMarkInfo(window.clientVars?.watermark));
        }
        const eventManager = SlideDataServices.SDKDataServices.EventManager();
        eventManager?.socketEvents.onWaterMarkChange((data) => {
            this.onWaterMarkChange(data.watermark);
        });
        this.initWithStore();
    }

    public getLazyWatermarkMask = (props?: any): React.ReactElement => {
        const { LazyWatermarkMask } = this;
        return <LazyBoundary>
            <LazyWatermarkMask {...props} watermarkManager={this} />
        </LazyBoundary>;
    };

    public getPlayerSVGInfo = () => new Promise((resolve) => {
        let watermarkInfo = { selfHide: false };
        if (window.clientVars?.watermark) {
            watermarkInfo = JSON.parse(window.clientVars.watermark);
        }
        const isDocOwner = window.clientVars.isOwner;
        const isSelfHide = watermarkInfo.selfHide;
        // 是文档所有者，且设置了对自己隐藏，则可以直接返回空数据，不渲染水印
        if (isDocOwner && isSelfHide) {
            resolve(null);
        }

        // playerSVGInfo存在水印数据，直接返回，不需要重新计算
        if ((this.playerSVGInfo?.base64Data || this.playerSVGInfo?.base64NickData)
            && this.clientVarsWaterMark === window.clientVars?.watermark) { // 添加watermark是否变化的判断
            resolve(this.playerSVGInfo);
        } else {
            this.clientVarsWaterMark = window.clientVars?.watermark;
            const { width, height } = screen;
            // 登陆但没有昵称信息的情况，通过接口拉取
            if (isLogin() && !window.clientVars.userName) {
                const url = `//${window.hostname}/cgi-bin/online_docs/user_info?get_vip_info=1`;
                net.ajax({
                    url,
                    xhrFields: { withCredentials: true },
                    ajaxType: 'get',
                    param: {
                        domain_id: window.clientVars.domainId,
                        pad_id: window.clientVars.padId,
                        xsrf: getXSRF(),
                    },
                    success: (res) => {
                        if (res.retcode === 0) {
                            // 昵称获取到以后，重新进行水印信息生成
                            window.clientVars.userName = res.nick;
                            this.generateWatermarkSVGWithSize(width, height).then((svgInfo) => {
                                this.playerSVGInfo = svgInfo;
                                resolve(this.playerSVGInfo);
                            });
                        }
                    },
                    error: () => {
                        window.log.error('[watermark] get_vip_info failed');
                    },
                });
            } else {
                this.generateWatermarkSVGWithSize(width, height).then((svgInfo) => {
                    this.playerSVGInfo = svgInfo;
                    resolve(this.playerSVGInfo);
                });
            }
        }
    });

    public initWithStore = () => {
        // 高级权限面版开启的监听
        window.SLR?.listen('SHOW_WATERMARK_FROM_ADVPER', (invokeInfo) => {
            this.showDialog(invokeInfo);
        });
    };

    public generateWatermarkSVG = async (info?) => {
        const { clientVars } = window;
        if (!clientVars) {
            window.log.error('[watermark] generateWatermarkSVG no_clientVars');
            return null;
        }

        let { watermark } = clientVars;
        if (typeof (info) === 'undefined') {
            if (this.watermarkSVGInfo) {
                // 没有新info，则不需要重新生成水印数据，返回已有的SVGInfo
                return this.watermarkSVGInfo;
            }
        } else {
            watermark = info;
        }

        if (!watermark) {
            // window.log.info('[watermark] generateWatermarkSVG no_clientVars.watermark');
            return null;
        }

        try {
            const markObj = JSON.parse(watermark);
            let hasVisit = false;
            if (_has(markObj, 's.private')) {
                hasVisit = markObj.s.private === 1;
            }
            // window.log.info(`[watermark] generateWatermarkSVG hasVisitMark ${hasVisit}`);
            this.nickName = clientVars.userName || _get(clientVars, 'userInfo.nick');
            if (this.nickName) {
                this.isNicknameUndefined = false;
            } else if (hasVisit) { // 有访客水印的时候，需要等昵称来
                // window.log.info(`[watermark] generateWatermarkSVG hasVisit: ${hasVisit} noUserName`);
                return null;
            }
        } catch (e) {
            window.log.error(`[watermark] generateWatermarkSVG wrong watermark ${e.toString()}`);
            return null;
        }

        this.updatePageParam();
        const pagesDocs = {
            width: this.pageWidth, // 页面宽度
            height: this.pageHeight, // 页面高度
        };

        const watermarkUsersDocs = {
            isAuthor: clientVars.isOwner, // 当前用户是否是作者（boolean）
            userName: this.nickName, // 当前用户的用户名（string）
            userDomain: clientVars.userDomain,
        };
        const watermarkFontSize = this.getWatermarkFontStyle();

        this.watermarkSVGInfo = GenerateSVG?.watermarkGetSVG?.(
            watermark,
            // @ts-ignore
            pagesDocs, watermarkUsersDocs, watermarkFontSize,
        );
        // window.log.info('[watermark] generateWatermarkSVG succeed', this.watermarkSVGInfo);
        return this.watermarkSVGInfo;
    };

    public showDialog = (invokeInfo?) => {
        let watermarkInfo = {
            text: '',
            marginType: 2,
            selfHide: 0,
            s: {
                private: 0, // 是否显示访客水印，0不显示，1显示
            },
            show: 1,
        };
        if (typeof (window.clientVars.watermark) !== 'undefined') {
            watermarkInfo = JSON.parse(window.clientVars.watermark);
        }
        const padId = SlideDataServices.DocInfoManager()?.globalPadId;
        const info = this.getShowDialogInfo(invokeInfo);
        if (WatermarkDialogManager) {
            WatermarkDialogManager.showDialog(info, JSON.stringify(watermarkInfo), padId!);
        } else {
            Snackbars.show({ message: i18n.t('请求失败，请稍后重试。'), autoClose: true, type: 'error' });
        }
    };

    public isShowGuestWaterMark(): boolean {
        return this.watermarkInfo.s?.private !== undefined && this.watermarkInfo.s.private === 1;
    }

    public getWaterMarkSizeForExportImage(): string {
        return waterMarkSizeForExportImage[this.watermarkInfo.marginType || 1];
    }

    /**
    * 接口测试用，设置水印信息的接口
    * @param {object} info
    * type:text 水印类型文本
    * text:水印信息
    * marginType: 1/2 宽松/紧密
    * selfHide: 0/1 对自己显示/隐藏
    * s:{private:0} 0/1 不显示/显示访客水印
    * show: 0/1 水印显示/隐藏
    */
    public setWatermarkToServer = (info) => {
        net.ajax({
            url: `https://${window.hostname}/dop-api/setmark`,
            type: 'POST',
            xhrFields: { withCredentials: true },
            param: {
                padId: SlideDataServices.DocInfoManager()?.globalPadId,
                mark: JSON.stringify(info),
                xsrf: getXSRF(),
            },
        });
    };

    private onWaterMarkChange(watermarkInfo) {
        if (watermarkInfo) {
            window.clientVars.watermark = watermarkInfo;
            this.updateWaterMarkInfo(watermarkInfo);
            this.updateWatermark(watermarkInfo);
        }
    }

    private updateWaterMarkInfo(watermarkString) {
        this.watermarkInfo = typeof watermarkString === 'string' ? JSON.parse(watermarkString) : {};
    }


    private updatePageParam = () => {
        if (!browser.isMobile) {
            const editZone = document.getElementById('select-view');
            if (editZone !== null) {
                const boundingInfo = editZone.getBoundingClientRect();
                if (boundingInfo.width > 0) {
                    this.pageWidth = boundingInfo.width;
                }
                if (boundingInfo.height > 0) {
                    this.pageHeight = boundingInfo.height;
                }
            }
        }
    };

    private generateWatermarkSVGWithSize = async (width, height) => {
        const { clientVars } = window;
        if (!clientVars) {
            return;
        }

        const { watermark } = clientVars;
        if (!watermark) {
            return null;
        }

        const pagesDocs = {
            width, // 指定宽度
            height, // 指定高度
        };

        this.nickName = clientVars.userName || _get(clientVars, 'userInfo.nick');
        if (typeof(this.nickName) !== 'undefined') {
            this.isNicknameUndefined = false;
        }

        const watermarkUsersDocs = {
            isAuthor: clientVars.isOwner, // 当前用户是否是作者（boolean）
            userName: this.nickName, // 当前用户的用户名（string）
            userDomain: clientVars.userDomain,
        };

        const watermarkFontSize = this.getWatermarkFontStyle();
        // 水印接口文档： https://docs.qq.com/doc/DTVBpQUhpRlVzckh4
        // @ts-ignore
        return GenerateSVG?.watermarkGetSVG?.(watermark, pagesDocs, watermarkUsersDocs, watermarkFontSize);
    };

    /**
     * @param {string} watermarkInfo
     */
    private updateWatermark = async (watermarkInfo) => {
        // 判定是否是创建者无需显示
        if (watermarkInfo.selfHide === 1) {
            return;
        }
        // 生成新的SVG信息，传入string值
        this.watermarkSVGInfo = await this.generateWatermarkSVG(watermarkInfo);
    };

    private getWatermarkFontStyle = () => ({
        watermarkFontColor: fontColor,
        // eslint-disable-next-line @tencent/i18next/no-literal-string
        watermarkFontFamily: '微软雅黑, TdocsUncommon',
    });

    private getShowDialogInfo = (invokeInfo) => {
        let info;
        let invokeFromMsg;
        if (typeof (invokeInfo) !== 'undefined') {
            invokeFromMsg = invokeInfo.detail.invokeFrom;
        } else {
            invokeFromMsg = 'TitleBar';
        }
        let wrapperDom;
        switch (invokeFromMsg) {
            case 'Share':
                wrapperDom = document.querySelector('.content-dialog-container');
                wrapperDom.style.visibility = 'hidden';
                info = {
                    invokeFrom: 'Share',
                    onClose: () => {
                        wrapperDom.style.visibility = 'visible';
                    },
                };
                break;
            case 'Cooperation':
                wrapperDom = document.querySelector('.content-dialog-container');
                wrapperDom.style.visibility = 'hidden';
                info = {
                    invokeFrom: 'Cooperation',
                    onClose: () => {
                        wrapperDom.style.visibility = 'visible';
                    },
                };
                break;
            case 'TitleBar':
                wrapperDom = document.querySelector('#avd-container');
                if (wrapperDom) wrapperDom.style.visibility = 'hidden';
                info = {
                    invokeFrom: 'TitleBar',
                    onClose: () => {
                        if (wrapperDom) wrapperDom.style.visibility = 'visible';
                    },
                };
                break;
            default:
                info = { invokeFrom: '' };
                break;
        }
        return info;
    };
}
